import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { meetingsApi } from '../../../services/api/meetingsApi';

/**
 * MeetingTypeFilterSelector - A reusable dropdown selector for filtering by meeting type
 * Fetches meeting types internally from the API
 *
 * @param {string|Array} value - The selected meeting type ID(s) (empty string/"All" for single, array for multiple)
 * @param {Function} onChange - Callback when selection changes (typeId or array of type objects) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} label - Label for the selector
 * @param {string} allLabel - Label for the "All" option
 * @param {string} size - Size of the select ('small' | 'medium')
 * @param {number} minWidth - Minimum width in pixels
 * @param {boolean} showAllOption - Whether to show the "All" option (single mode only)
 * @param {Array} limitToTypeIds - Optional array of type IDs to limit the options
 * @param {boolean} multiple - Enable multi-select mode
 * @param {string} placeholder - Placeholder text when no items selected (multi mode)
 * @param {Object} sx - Additional styles for the FormControl
 */
const MeetingTypeFilterSelector = ({
  value = '',
  onChange,
  disabled = false,
  label = 'Meeting Type',
  allLabel = 'All Types',
  size = 'small',
  minWidth = 180,
  showAllOption = true,
  limitToTypeIds = null,
  multiple = false,
  placeholder = 'Meeting types...',
  sx = {},
}) => {
  const [meetingTypes, setMeetingTypes] = useState([]);

  useEffect(() => {
    const fetchMeetingTypes = async () => {
      try {
        const data = await meetingsApi.getMeetingTypes();
        setMeetingTypes(data || []);
      } catch (err) {
        console.error('Failed to fetch meeting types:', err);
      }
    };
    fetchMeetingTypes();
  }, []);

  // Filter meeting types if limitToTypeIds is provided
  const displayedTypes = limitToTypeIds
    ? meetingTypes.filter(mt => limitToTypeIds.includes(mt.id))
    : meetingTypes;

  // Multi-select mode
  if (multiple) {
    const selectedIds = (value || []).map(v => v.id);

    const handleMultiChange = (event) => {
      const selectedValues = event.target.value;
      // Convert IDs back to full objects
      const selectedObjects = selectedValues
        .map(id => displayedTypes.find(t => t.id === id))
        .filter(Boolean);
      onChange(selectedObjects);
    };

    return (
      <FormControl size={size} sx={{ minWidth, ...sx }} disabled={disabled}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select
          multiple
          value={selectedIds}
          onChange={handleMultiChange}
          label={label}
          displayEmpty
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <em style={{ color: '#9CA3AF' }}>{placeholder}</em>;
            }
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const type = displayedTypes.find(t => t.id === id);
                  if (!type) return null;
                  return (
                    <Chip
                      key={id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: type.color || '#6b7280',
                              flexShrink: 0,
                            }}
                          />
                          {type.name}
                        </Box>
                      }
                      size="small"
                      onDelete={() => {
                        const newValue = (value || []).filter(v => v.id !== id);
                        onChange(newValue);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                })}
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {displayedTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: type.color || '#6b7280',
                    flexShrink: 0,
                  }}
                />
                <ListItemText primary={type.name} />
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Single-select mode (original behavior)
  return (
    <FormControl size={size} sx={{ minWidth, ...sx }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
      >
        {showAllOption && (
          <MenuItem value="">{allLabel}</MenuItem>
        )}
        {displayedTypes.map((type) => (
          <MenuItem key={type.id} value={type.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: type.color || '#6b7280',
                  flexShrink: 0,
                }}
              />
              {type.name}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MeetingTypeFilterSelector;
